import React, { Component } from 'react';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import _differenceBy from 'lodash/differenceBy';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import _isNumber from 'lodash/isNumber';

import Button from '@material-ui/core/Button';

import { AppContext } from '../../AppProvider';
import { Table as DvExTable } from '@devexpress/dx-react-grid-material-ui';
import Table from '../../components/Global/Table/Table';
import AccessorialsTableMetadata from './AccessorialsTable.metadata';
import ErrorNotification from '../../components/common/ErrorNotification';
import AccessorialCreateForm from '../../components/accessorial/CreateForm';
import AccessorialEditForm from '../../components/accessorial/EditForm';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import { DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';

import * as Queries from '../../graphQL/queries/orgRefOrgAccessorialService';
import * as Mutations from '../../graphQL/mutations/orgRefOrgAccessorialService';
import { accessorialServices as accessorialServicesQuery } from '../../graphQL/queries/referenceDataQueries';

import { sanitize } from '../../Models/Accessorial';

const styles = theme => ({
  titleWrapper: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing.unit * 5
  },
  title: {
    flex: 1
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(12,31,44, 0.04)'
    }
  }
});

export class AccessorialsIndex extends Component {
  state = {
    rowsSelected: [],
    items: [],
    item: {},
    loading: true,
    error: null,
    errorsOnSave: null,
    isOpenedContactFormModal: false,
    contact: null,
    isSubmitting: false,
    page: 0,
    rowsPerPage: localStorage.getItem('PropelRowsPerPage')
      ? parseInt(localStorage.getItem('PropelRowsPerPage'), 10)
      : DEFAULT_ROWS_PER_PAGE,
    totalCount: 0,
    deleting: false,
    accessorialServices: [],
    availableAccessorialsList: [],
    displayCreateForm: false,
    displayEditForm: false
  };

  async componentDidMount() {
    await this._getList();
    await this._getAccessorials();
  }

  _getAccessorials = async () => {
    const { client: ApolloClient } = this.props;
    try {
      const {
        data: {
          referenceData: { accessorialServices }
        }
      } = await ApolloClient.query({
        query: accessorialServicesQuery
      });

      this.setState(
        {
          accessorialServices
        },
        this._getAvailableAccessorials
      );
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  };

  _getList = async () => {
    const { client: ApolloClient } = this.props;
    const { currentOrgId } = this.context.globalProps;
    const { page, rowsPerPage } = this.state;
    const offset = page * rowsPerPage;

    try {
      const {
        data: {
          orgRef_OrgAccessorialService: {
            paged: { results: items, totalCount }
          }
        }
      } = await ApolloClient.query({
        query: Queries.paged,
        variables: {
          orgId: currentOrgId,
          limit: rowsPerPage,
          offset
        },
        fetchPolicy: 'network-only'
      });

      this.setState({
        items: AccessorialsTableMetadata.formatData(items),
        totalCount: _isNumber(totalCount) ? totalCount : this.state.totalCount
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  };

  _getAvailableAccessorials = () => {
    const selectedAccessorials = this.state.items.map(item => ({
      enumValue: item.accessorialServiceId
    }));
    const availableAccessorialsList = _differenceBy(
      this.state.accessorialServices,
      selectedAccessorials,
      'enumValue'
    ).map(accessorialService => ({
      value: accessorialService.enumValue,
      title: accessorialService.description
    }));
    this.setState({
      availableAccessorialsList
    });
  };

  _handleOpenAddForm = () => this.setState({ displayCreateForm: true });

  _handleOpenEditForm = () => this.setState({ displayEditForm: true });

  _handleRowClick = row =>
    this.setState({ item: row }, this._handleOpenEditForm);

  _handleCloseAddForm = () => this.setState({ displayCreateForm: false });

  _handleCloseEditForm = () => this.setState({ displayEditForm: false });

  _handleCreate = async values => {
    const { currentOrgId } = this.context.globalProps;

    try {
      this.setState({ isSubmitting: true });
      const { client: ApolloClient } = this.props;
      await Promise.all(
        values.accessorials.map(async value => {
          const accessorial = _find(this.state.availableAccessorialsList, {
            value: value
          });

          return await ApolloClient.mutate({
            mutation: Mutations.create,
            variables: {
              orgAccessorialService: {
                orgId: currentOrgId,
                accessorialServiceId: value,
                name: accessorial.title
              }
            }
          });
        })
      );
      await this._getList();
      this.context.resyncGlobalProps();
      this._getAvailableAccessorials();
    } catch (errorsOnSave) {
      this.setState({
        errorsOnSave
      });
      throw errorsOnSave;
    } finally {
      this.setState({ isSubmitting: false });
    }
  };
  _handleUpdate = async values => {
    try {
      this.setState({ isSubmitting: true });
      const { client: ApolloClient } = this.props;

      await ApolloClient.mutate({
        mutation: Mutations.update,
        variables: {
          orgAccessorialService: {
            ...sanitize(values)
          }
        }
      });

      await this._getList();
      this.context.resyncGlobalProps();
    } catch (errorsOnSave) {
      this.setState({
        errorsOnSave
      });
      throw errorsOnSave;
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  _handleSelectRow = rowsSelected => this.setState({ rowsSelected });

  _handleDelete = async () => {
    this.setState({ deleting: true });
    const { client: ApolloClient } = this.props;

    const { items, rowsSelected } = this.state;
    const itemsSelected = _filter(items, (item, index) =>
      rowsSelected.includes(index)
    );
    const itemsIdSelected = itemsSelected.map(item => item.id);
    try {
      await Promise.all(
        itemsIdSelected.map(
          async itemId =>
            await ApolloClient.mutate({
              mutation: Mutations.deleteItems,
              variables: { orgAccessorialServiceId: parseInt(itemId) }
            })
        )
      );
      await this._getList();
      this.context.resyncGlobalProps();
      this._getAvailableAccessorials();
    } catch (error) {
      // this.setState({error});
    } finally {
      this.setState({ deleting: false, rowsSelected: [] });
    }
  };

  _changePage = page => {
    this.setState({ page, loading: true }, this._getList);
  };

  _changeRowsPerPage = rowsPerPage => {
    localStorage.setItem('PropelRowsPerPage', rowsPerPage);
    this.setState({ rowsPerPage: rowsPerPage, page: 0 });
    const rows = rowsPerPage === 0 ? DEFAULT_ROWS_PER_PAGE : rowsPerPage;
    this.setState({ rowsPerPage: rows, page: 0, loading: true }, this._getList);
  };

  render() {
    const { t, classes } = this.props;

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    return (
      <div>
        <div className={classes.titleWrapper}>
          <h1
            className={classnames(
              classes.headline4,
              classes.noMarginBottom,
              classes.title
            )}
          >
            {t('Accessorials')}
          </h1>
          <Button
            className={classes.button}
            classes={{
              label: classes.buttonLabel
            }}
            variant="contained"
            color="primary"
            onClick={this._handleOpenAddForm}
          >
            {t('Add Accessorials')}
          </Button>
        </div>
        <div>
          <Table
            name={t('Accessorial')}
            columns={AccessorialsTableMetadata.columns({ t })}
            data={this.state.items}
            rowComponent={props => (
              <DvExTable.Row
                {...props}
                className={classes.tableRow}
                onClick={() => this._handleRowClick(props.row)}
              />
            )}
            page={this.state.page}
            rowsPerPage={this.state.rowsPerPage}
            totalCount={this.state.totalCount}
            loading={this.state.loading}
            withDownload={false}
            withColumnChooser={false}
            withSelection={true}
            withServerPaging={true}
            rowsSelected={this.state.rowsSelected}
            onSelectRow={this._handleSelectRow}
            onDelete={this._handleDelete}
            deleting={this.state.deleting}
            onChangePage={this._changePage}
            onChangePageRowsPerPage={this._changeRowsPerPage}
          />
        </div>
        <AccessorialCreateForm
          open={this.state.displayCreateForm}
          availableAccessorialsList={this.state.availableAccessorialsList}
          onClose={this._handleCloseAddForm}
          onSave={this._handleCreate}
          isSubmitting={this.state.isSubmitting}
        />
        <AccessorialEditForm
          open={this.state.displayEditForm}
          onClose={this._handleCloseEditForm}
          onSave={this._handleUpdate}
          isSubmitting={this.state.isSubmitting}
          accessorial={this.state.item}
        />
        {this.state.errorsOnSave && (
          <ErrorNotification message={this.state.errorsOnSave.message} />
        )}
      </div>
    );
  }
}

AccessorialsIndex.contextType = AppContext;
const AccessorialsIndexWithTranslation = withNamespaces()(AccessorialsIndex);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    styles
  )
)(withApollo(AccessorialsIndexWithTranslation));
